/**
 * Backend API Service
 *
 * Communicates with the PiPar backend for cloud storage mode.
 * Follows patterns from cost_test_client.py (per-endpoint methods, SSE parsing, retry).
 */

import type { ChatEvent, ChatSession, ChatAttachment } from '../types'
import { getIdToken } from './auth'
import { PRIVATE_MODE, PRIVATE_PERSONA, PRIVATE_MODEL } from './privateConfig'
import { API_BASE } from './backendConfig'

/**
 * Default chat model — a PiPar tier alias, never a provider id.
 *
 * The backend re-points these aliases at better models over time, and they are
 * the one family included in EVERY subscription tier: `model_access.yaml`
 * holds free and Starter to the PiPar family plus the free open-weight models,
 * so a provider id here (this used to be `gemini-3-flash`) meant every message
 * from a restricted tier came back refused. `pipar-premium` is the balanced
 * star and matches the mobile app's `Config.defaultChatModel`.
 */
export const DEFAULT_CHAT_MODEL = 'pipar-premium'
// Not the same thing as the REFUSAL fallback. This is what a session runs on
// when nothing has been chosen; `fallback_model` (pipar-basic, from
// model_access.yaml via getFallbackModel()) is where a refused model is sent.
// Keep them distinct — the second must be the cheap star the server clamps to.

/**
 * Title generation, via /completions.
 *
 * NOT a PiPar alias, deliberately. /completions pins the OpenRouter client for
 * every non-`gpt` model, while `pipar-*` resolves through DEEPSEEK_FLASH_ROUTE
 * to whichever host is selected (Fireworks by default) — the resolved id would
 * be sent to the wrong provider. This id takes the endpoint's Gemini branch,
 * which works. It is listed in `extra_allowed` in the backend's
 * model_access.yaml so titles keep working on free and Starter.
 */
export const DEFAULT_TITLE_MODEL = 'google/gemini 3.1 flash-lite'

async function authHeaders(contentType = 'application/json'): Promise<Record<string, string>> {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
  if (contentType) headers['Content-Type'] = contentType
  return headers
}

async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries = 3,
  retryDelay = 2000,
): Promise<Response> {
  let lastError: Error | null = null
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetch(url, options)
    } catch (e) {
      lastError = e as Error
      if (attempt < maxRetries) {
        console.warn(`Retry ${attempt + 1}/${maxRetries} after ${lastError.message}`)
        await new Promise((r) => setTimeout(r, retryDelay))
      }
    }
  }
  throw lastError
}

// ── Session Management ──────────────────────────────────────────

/**
 * Client protocol version sent to /voice_chat/start. Version 2 = this client
 * honors a `chat_url` in the /start response and sends /chat turns wherever
 * it points (compute engine while it has free text chat slots, Cloud Run
 * when it's full). Bump when adding support for new version-gated behaviors.
 */
const PROTOCOL_VERSION = 2

/**
 * Backend-assigned base URL for this session's /chat turns, from the
 * /voice_chat/start response. Null → API_BASE (no routing in play, e.g.
 * dev backend). Session management endpoints (start/stop) always use
 * API_BASE — only /chat follows this.
 */
let sessionChatBase: string | null = null

export interface StartChatParams {
  personaId: string
  sessionType?: string
  /** The language the AI answers in (voice sessions: the voice language), e.g. "fi-FI". */
  language?: string
  discussionId?: string
  messages?: Array<Record<string, string>>
  deviceId?: string
  /** Chat model the session will run on. Sent to /voice_chat/start so the
   *  backend can opt the session into Gemini explicit caching at start
   *  (caching is keyed to the model). Should match the model the caller
   *  passes to sendMessage(). Defaults to the same sendMessage default. */
  model?: string
  /** Persist the conversation to cloud storage. Defaults to true; quick chat
   *  sends false so the exchange leaves no session or messages behind. The
   *  backend forces this off for quick_chat sessions regardless. */
  saveToCloud?: boolean
}

export async function startChat(params: StartChatParams): Promise<{
  voice_chat_id: string
  cloud_memory?: Record<string, string>
  needs_tools?: boolean
  /** Voice sessions: the voice engine that serves the call (production). */
  session_url?: string
}> {
  const headers = await authHeaders()
  const now = new Date()

  // Pull user's web settings so image-gen preferences reach the backend
  // cloud tool executor via the session. Without this, the backend falls back
  // to its own defaults (`_DEFAULT_IMAGE_MODEL` in cloud_tool_executor.py —
  // gemini-3.1-flash-lite-image — and 1:1) regardless of what the user saved
  // in Settings. Private mode doesn't run backend tools, so skip the fetch.
  const webSettings: Record<string, unknown> = PRIVATE_MODE
    ? {}
    : await loadUserSettings('web').catch(() => ({}))
  const imageModel =
    (webSettings.image_model as string) ||
    (webSettings.image_gen_model as string) ||
    undefined
  const imageAspectRatio = (webSettings.image_aspect_ratio as string) || undefined

  // Default matches the sendMessage default so cache creation at /start
  // sees the same model the first /chat call will use.
  const sessionModel = params.model || DEFAULT_CHAT_MODEL

  const payload = PRIVATE_MODE
    ? {
        discussion_id: params.discussionId || `private_${Date.now()}`,
        messages: params.messages || [],
        session_type: params.sessionType || 'text_chat',
        use_server_prompts: true,
        active_persona_id: params.personaId || PRIVATE_PERSONA,
        model: sessionModel,
        protocol_version: PROTOCOL_VERSION,
      }
    : {
        discussion_id: params.discussionId || `cloud_${Date.now()}`,
        messages: params.messages || [
          {
            role: 'system',
            content: 'You are a helpful AI assistant.',
          },
        ],
        session_type: params.sessionType || 'text_chat',
        ...(params.language ? { language: params.language } : {}),
        save_to_cloud: params.saveToCloud !== false,
        server_side_tools: true,
        device_id: params.deviceId || 'web-client',
        active_persona_id: params.personaId,
        model: sessionModel,
        // The custom persona has no fixed personality description in
        // persona_descriptions.json — only a placeholder. Without this flag,
        // the backend takes the built-in branch in build_message_2 and
        // wraps the user's customization with that placeholder. Sending it
        // explicitly keeps things correct even if the backend's
        // active_persona_id-based fallback isn't deployed yet.
        is_custom_persona: params.personaId === 'custom',
        max_context_size: 64000,
        context_size: 0,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        tools: [],
        use_server_prompts: true,
        model_tier: 'basic',
        tool_proxy_enabled: true,
        has_subscription: true,
        protocol_version: PROTOCOL_VERSION,
        ...(imageModel ? { image_model: imageModel } : {}),
        ...(imageAspectRatio ? { image_aspect_ratio: imageAspectRatio } : {}),
        daily_context: {
          current_date: now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
          current_time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }

  const res = await fetchWithRetry(`${API_BASE}/voice_chat/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Start failed (${res.status}): ${error.slice(0, 200)}`)
  }

  const data = await res.json()
  // Backend-assigned /chat host for this session (text chat engine/Cloud Run
  // routing). Absent on backends without routing configured → API_BASE.
  // Ignored in Vite dev mode: dev uses relative URLs through the proxy, and
  // an absolute chat_url would bypass it (CORS).
  sessionChatBase =
    !import.meta.env.DEV && typeof data.chat_url === 'string' && data.chat_url
      ? data.chat_url
      : null
  if (sessionChatBase) console.info(`Session chat URL: ${sessionChatBase}`)
  return data
}

export async function stopChat(
  voiceChatId: string,
  personaName?: string,
): Promise<{
  messages?: Array<Record<string, string>>
  discussion_id?: string
}> {
  const headers = await authHeaders()
  const payload = {
    voice_chat_id: voiceChatId,
    update_memory: false,
    persona_name: personaName,
  }

  const res = await fetchWithRetry(`${API_BASE}/voice_chat/stop`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })

  sessionChatBase = null // Session over — next session gets a fresh assignment

  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Stop failed (${res.status}): ${error.slice(0, 200)}`)
  }

  return res.json()
}

/**
 * Fire-and-forget stop for unload paths (pagehide / beforeunload). Without
 * this, closing the tab leaves messages in Redis until TTL expiry — they
 * never reach `cloud_chat_messages` and mobile sees an empty chat.
 *
 * Uses fetch keepalive so the browser keeps the request alive after the
 * page is gone. sendBeacon would also work but can't set Authorization.
 */
export function stopChatBeacon(voiceChatId: string, personaName?: string): void {
  authHeaders()
    .then((headers) => {
      const body = JSON.stringify({
        voice_chat_id: voiceChatId,
        update_memory: false,
        persona_name: personaName,
      })
      fetch(`${API_BASE}/voice_chat/stop`, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      }).catch(() => {})
    })
    .catch(() => {})
}

// ── Chat (SSE streaming) ────────────────────────────────────────

export async function* sendMessage(
  voiceChatId: string,
  message: string,
  model: string = DEFAULT_CHAT_MODEL,
  attachments?: ChatAttachment[],
): AsyncGenerator<ChatEvent> {
  const headers = await authHeaders()
  const effectiveModel = PRIVATE_MODE ? (PRIVATE_MODEL || model) : model
  // The backend's chat handler reads attachments from each message
  // (see `format_message_for_openai` in routes/chat.py): images are inlined as
  // image_url blocks, text/plain & text/html are decoded into text blocks,
  // application/pdf is forwarded as a document block. Strip the local-only
  // previewUrl before sending so we don't bloat the payload.
  const wireAttachments = attachments?.map((a) => ({
    fileName: a.fileName,
    mimeType: a.mimeType,
    data: a.data,
  }))
  const userMsg: Record<string, unknown> = {
    role: 'user',
    content: message,
  }
  if (wireAttachments && wireAttachments.length > 0) {
    userMsg.attachments = wireAttachments
  }
  const hasMessage = !!message || (wireAttachments && wireAttachments.length > 0)
  const payload = {
    model: effectiveModel,
    voice_chat_id: voiceChatId,
    messages: hasMessage ? [userMsg] : [],
    chat_type: PRIVATE_MODE ? 'basic' : 'openai',
    streaming: true,
    use_server_prompts: PRIVATE_MODE ? true : undefined,
    active_persona_id: PRIVATE_MODE ? PRIVATE_PERSONA : undefined,
    client_message_id: `tc_${Date.now()}`,
  }

  const res = await fetchWithRetry(`${sessionChatBase ?? API_BASE}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const error = await res.text()
    yield { type: 'error', data: `HTTP ${res.status}: ${error.slice(0, 200)}` }
    return
  }

  const reader = res.body?.getReader()
  if (!reader) {
    yield { type: 'error', data: 'Streaming not supported' }
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      const payload = line.slice(6)
      if (payload === '[DONE]') return

      try {
        const event: ChatEvent = JSON.parse(payload)
        yield event
      } catch {
        // Skip malformed SSE chunks
      }
    }
  }
}

// ── Memory ──────────────────────────────────────────────────────

export interface UpdateMemoryParams {
  discussionId: string
  messageHistory: Array<Record<string, string>>
  personaName: string
  /** Stable persona id used by the backend to partition cloud memory.
   * For custom personas the display name can change but the id stays
   * "custom" — without this, the memory row written here won't match
   * what the session load / cloud tools read. */
  personaId: string
  userDetails?: string
  diary?: string
  note?: string
  futureEvents?: string
}

export async function updateMemory(params: UpdateMemoryParams): Promise<Record<string, unknown>> {
  const headers = await authHeaders()
  const payload = {
    discussion_id: params.discussionId,
    message_history: params.messageHistory,
    persona_name: params.personaName,
    persona_id: params.personaId,
    user_details: params.userDetails || '',
    diary: params.diary || '',
    note: params.note || '',
    future_events: params.futureEvents || '',
    save_to_cloud: true,
    // Web has no local memory store — let the backend fill in any empty
    // fields from cloud_user_memory before the AI runs, so an empty payload
    // doesn't clobber existing user_details/diary/note/future_events.
    use_cloud_baseline: true,
    model: 'gpt-4o-mini',
    current_date: new Date().toISOString().split('T')[0],
    current_time: new Date().toTimeString().split(' ')[0].slice(0, 5),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }

  const res = await fetchWithRetry(`${API_BASE}/voice_chat/update_memory`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Update memory failed (${res.status}): ${error.slice(0, 200)}`)
  }

  return res.json()
}

// ── Memory Search (direct, no AI) ──────────────────────────────

export interface SearchMemoryResult {
  text: string
  similarity: number
  source: string
  session_id?: string
  persona?: string
  created_at?: string | null
}

/**
 * Direct semantic search of cloud_message_embeddings + cloud_diary_embeddings.
 * Bypasses chat / AI tool-call roundtrip used previously by the search panel.
 *
 * When [summarize] is true, the backend also runs a lightweight Gemini call
 * over the matched chunks and returns a natural-language `summary` answering
 * the query. The raw `results` are still included so the caller can show
 * supporting chunks alongside the summary.
 */
export async function searchMemory(
  query: string,
  personaId?: string,
  summarize = false,
): Promise<{ results: SearchMemoryResult[]; summary?: string; message?: string }> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(`${API_BASE}/voice_chat/search_memory`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, persona_id: personaId, summarize }),
  })
  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Search memory failed (${res.status}): ${error.slice(0, 200)}`)
  }
  const data = await res.json()
  return {
    results: Array.isArray(data.results) ? data.results : [],
    summary: typeof data.summary === 'string' ? data.summary : undefined,
    message: data.message,
  }
}

// ── Cloud Personas ─────────────────────────────────────────────

export interface CloudPersona {
  id: string
  persona_name: string
  /** Short role tagline — user-customizable for the custom persona. */
  role?: string
  /** 2–3 sentence description shown to other personas. Custom persona only. */
  description?: string
  user_details: string
  diary: string
  /** Per-user persona personality customization. The fixed personality
   *  (per persona id, same for all users) lives in
   *  config/prompts/persona_descriptions.json on the backend and is
   *  fetched separately via getPersonaDescription. */
  note: string
  future_events: string
  chat_model: string
  tts_voice: string
  tts_provider: string
  tts_speaking_style: string
  speaking_speed: number
}

/** One archived diary entry (one specific day) returned by the cloud. */
export interface CloudDiaryEntry {
  persona_id: string
  /** YYYY-MM-DD */
  date: string
  content: string
  tags: string[]
  created_at?: string | null
  updated_at?: string | null
}

/**
 * Fetch archived diary entries for a persona. Mirrors the mobile
 * `DailyDiaryManager.loadDiaryEntriesForMonth` shape. Without `month`,
 * returns the full archive newest-first; with `month=YYYY-MM`, narrows
 * to that month. The response also includes the distinct months that
 * have entries, so the UI can render its month-chip row in one round
 * trip instead of two.
 *
 * Persona ids marked Always Local on mobile never reach the cloud, so
 * an empty result there is the expected outcome — not an error.
 */
export async function listCloudDiaryEntries(
  personaId: string,
  month?: string,
): Promise<{ entries: CloudDiaryEntry[]; months: string[] }> {
  if (!personaId) return { entries: [], months: [] }
  try {
    const headers = await authHeaders()
    const params = new URLSearchParams({ persona_id: personaId })
    if (month) params.set('month', month)
    const res = await fetchWithRetry(`${API_BASE}/cloud_data/diary_entries?${params}`, {
      method: 'GET',
      headers,
    })
    if (!res.ok) {
      if (res.status === 404) return { entries: [], months: [] }
      return { entries: [], months: [] }
    }
    const data = await res.json()
    return {
      entries: Array.isArray(data?.entries) ? data.entries : [],
      months: Array.isArray(data?.months) ? data.months : [],
    }
  } catch {
    return { entries: [], months: [] }
  }
}

/** Upsert one archived diary entry. Used by the web-side editor. */
export async function upsertCloudDiaryEntry(
  personaId: string,
  entryDate: string,
  content: string,
  tags?: string[],
): Promise<boolean> {
  if (!personaId || !entryDate) return false
  try {
    const headers = await authHeaders()
    const body: Record<string, unknown> = { content }
    if (tags) body.tags = tags
    const res = await fetchWithRetry(
      `${API_BASE}/cloud_data/diary_entries/${encodeURIComponent(personaId)}/${encodeURIComponent(entryDate)}`,
      { method: 'PUT', headers, body: JSON.stringify(body) },
    )
    return res.ok
  } catch {
    return false
  }
}

/** Delete one archived diary entry. Treats 404 as success. */
export async function deleteCloudDiaryEntry(
  personaId: string,
  entryDate: string,
): Promise<boolean> {
  if (!personaId || !entryDate) return false
  try {
    const headers = await authHeaders()
    const res = await fetchWithRetry(
      `${API_BASE}/cloud_data/diary_entries/${encodeURIComponent(personaId)}/${encodeURIComponent(entryDate)}`,
      { method: 'DELETE', headers },
    )
    return res.ok || res.status === 404
  } catch {
    return false
  }
}

/** Update a single field of cloud_user_memory for a persona. */
export async function updatePersonaMemoryField(
  personaId: string,
  field: 'user_details' | 'diary' | 'note' | 'future_events',
  content: string,
): Promise<boolean> {
  if (!personaId) return false
  try {
    const headers = await authHeaders()
    const res = await fetchWithRetry(`${API_BASE}/cloud_data/persona_memory_field`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ persona_id: personaId, field, content }),
    })
    return res.ok
  } catch {
    return false
  }
}

export async function listCloudPersonas(): Promise<CloudPersona[]> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(`${API_BASE}/voice_chat/cloud_personas`, {
    method: 'GET',
    headers,
  })
  if (!res.ok) {
    if (res.status === 404) return []
    const error = await res.text()
    throw new Error(`List personas failed (${res.status}): ${error.slice(0, 200)}`)
  }
  const data = await res.json()
  return data.personas || []
}

/**
 * Fetch the built-in personality definition for a persona. The endpoint
 * is gated server-side to owner-tier accounts (returns 403 otherwise) —
 * so a null result means either the user isn't an owner, the persona id
 * is unknown, or the network failed. Callers should treat null as
 * "don't show the built-in section" rather than as an error.
 */
export async function getPersonaDescription(personaId: string): Promise<string | null> {
  if (!personaId) return null
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/debug/persona/${encodeURIComponent(personaId)}?full=true`, {
      method: 'GET',
      headers,
    })
    if (!res.ok) return null
    const data = await res.json()
    return typeof data?.description === 'string' && data.description ? data.description : null
  } catch {
    return null
  }
}

// ── Cloud Sessions ──────────────────────────────────────────────

export async function listCloudSessions(
  persona?: string,
  limit = 50,
  offset = 0,
): Promise<{ sessions: ChatSession[] }> {
  const headers = await authHeaders()
  const params = new URLSearchParams()
  if (persona) params.set('persona', persona)
  params.set('limit', String(limit))
  params.set('offset', String(offset))

  const res = await fetchWithRetry(
    `${API_BASE}/voice_chat/cloud_sessions?${params}`,
    { method: 'GET', headers },
  )

  if (!res.ok) {
    // Endpoint may not exist yet (Step 2) — return empty
    if (res.status === 404 || res.status === 405) {
      return { sessions: [] }
    }
    const error = await res.text()
    throw new Error(`List sessions failed (${res.status}): ${error.slice(0, 200)}`)
  }

  return res.json()
}

/**
 * Lightweight check: have any sessions changed since a timestamp?
 * Returns { up_to_date: true } or { sessions, changes, sync_ts }.
 */
export async function checkCloudSessions(
  persona: string,
  since: string,
): Promise<{
  up_to_date: boolean
  sessions?: Array<Record<string, unknown>>
  changes?: Array<Record<string, unknown>>
  sync_ts?: string
}> {
  const headers = await authHeaders()
  const params = new URLSearchParams({ persona, since })

  const res = await fetchWithRetry(
    `${API_BASE}/voice_chat/cloud_sessions/check?${params}`,
    { method: 'GET', headers },
  )

  if (!res.ok) {
    // Endpoint may not exist yet — treat as stale
    if (res.status === 404 || res.status === 405) {
      return { up_to_date: false }
    }
    return { up_to_date: false }
  }

  return res.json()
}

export async function loadCloudSession(
  sessionId: string,
): Promise<{ session: ChatSession; messages: Array<Record<string, string>> }> {
  const headers = await authHeaders()

  const res = await fetchWithRetry(
    `${API_BASE}/voice_chat/cloud_sessions/${sessionId}`,
    { method: 'GET', headers },
  )

  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Load session failed (${res.status}): ${error.slice(0, 200)}`)
  }

  return res.json()
}

export async function deleteCloudSession(sessionId: string): Promise<void> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(
    `${API_BASE}/voice_chat/cloud_sessions/${sessionId}/modify`,
    { method: 'POST', headers, body: JSON.stringify({ action: 'delete' }) },
  )
  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Delete session failed (${res.status}): ${error.slice(0, 200)}`)
  }
}

export async function renameCloudSession(
  sessionId: string,
  summary: string,
): Promise<void> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(
    `${API_BASE}/voice_chat/cloud_sessions/${sessionId}/modify`,
    { method: 'POST', headers, body: JSON.stringify({ action: 'rename', summary }) },
  )
  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Rename session failed (${res.status}): ${error.slice(0, 200)}`)
  }
}

// ── Title Generation ───────────────────────────────────────────

export async function generateTitle(
  userMessage: string,
  assistantMessage: string,
): Promise<string | null> {
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/completions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model: DEFAULT_TITLE_MODEL,
        messages: [
          { role: 'system', content: 'You are a helpful assistant that creates short, descriptive titles.' },
          {
            role: 'user',
            content: `Create a short title (max 6 words) for a conversation that starts with this exchange. Reply with ONLY the title, no quotes or punctuation:\n\nUser: ${userMessage}\nAssistant: ${assistantMessage.slice(0, 200)}`,
          },
        ],
        max_tokens: 30,
        temperature: 0.5,
      }),
    })
    if (!res.ok) {
      console.warn('Title generation failed:', res.status, await res.text().catch(() => ''))
      return null
    }
    const data = await res.json()
    const title = data?.choices?.[0]?.message?.content?.trim()
    console.log('Generated title:', title)
    return title || null
  } catch (e) {
    console.warn('Title generation error:', e)
    return null
  }
}

// ── Account Management ───────────────────────────────────────────

/**
 * Ensure the authenticated user has a Pipar account.
 * Calls create_user which is a no-op for existing users ("existing user found")
 * and creates a new account with trial for new users.
 * Returns true if the user can proceed (account exists or was just created).
 */
export async function ensureUserAccount(email: string): Promise<boolean> {
  try {
    const headers = await authHeaders()
    const body: Record<string, unknown> = {
      email,
      fcm_token: '',
      start_trial: true,
    }
    const trialPassword = import.meta.env.VITE_TRIAL_PASSWORD
    if (trialPassword) {
      (headers as Record<string, string>)['X-Trial-Password'] = trialPassword
    }
    const res = await fetch(`${API_BASE}/user/create_user`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
    if (!res.ok) return false
    const data = await res.json()
    // All valid statuses: existing user, newly created, or restored from deletion
    return data?.status === 'existing user found'
      || data?.status === 'user created'
      || data?.status === 'user restored'
  } catch {
    return false
  }
}

// ── User Settings ────────────────────────────────────────────────

/** Load user settings from backend. client_type: 'mobile' | 'web' | 'desktop' */
export async function loadUserSettings(clientType: string = 'web'): Promise<Record<string, unknown>> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(
    `${API_BASE}/cloud_data/settings?client_type=${encodeURIComponent(clientType)}`,
    { method: 'GET', headers },
  )
  if (!res.ok) return {}
  const data = await res.json()
  return data?.settings || {}
}

/** Save user settings to backend (partial merge). */
export async function saveUserSettings(settings: Record<string, unknown>, clientType: string = 'web'): Promise<boolean> {
  try {
    const headers = await authHeaders()
    const res = await fetch(
      `${API_BASE}/cloud_data/settings?client_type=${encodeURIComponent(clientType)}`,
      { method: 'PUT', headers, body: JSON.stringify({ data: settings }) },
    )
    return res.ok
  } catch {
    return false
  }
}

/** Save persona settings (voice, model, name, etc.) to backend. */
export async function savePersonaSettings(persona: {
  id: string
  persona_name?: string
  chat_model?: string
  tts_voice?: string
  tts_provider?: string
  tts_speaking_style?: string
  speaking_speed?: number
}): Promise<boolean> {
  try {
    const headers = await authHeaders()
    const res = await fetch(
      `${API_BASE}/cloud_data/persona_settings/${encodeURIComponent(persona.id)}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          data: {
            persona_name: persona.persona_name || '',
            chat_model: persona.chat_model || '',
            tts_voice: persona.tts_voice || '',
            tts_provider: persona.tts_provider || '',
            tts_speaking_style: persona.tts_speaking_style || '',
            speaking_speed: persona.speaking_speed ?? 1.0,
          },
        }),
      },
    )
    return res.ok
  } catch {
    return false
  }
}

// ── Image Generation ────────────────────────────────────────────

export interface ImageGenResult {
  success: boolean
  /** Base64-encoded PNG bytes (no data URL prefix). */
  imageData?: string
  revisedPrompt?: string
  error?: string
}

/**
 * Generate an image via the backend's /image_gen endpoint.
 * Mirrors the mobile app's ImageGenerationTools.generateImage call.
 * `model` and `aspectRatio` come from the user's AI Services settings.
 */
export async function generateImage(
  prompt: string,
  model: string,
  aspectRatio: string,
): Promise<ImageGenResult> {
  if (!prompt.trim()) {
    return { success: false, error: 'Prompt is required' }
  }
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/image_gen`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        prompt: prompt.trim(),
        model,
        aspect_ratio: aspectRatio,
      }),
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      return { success: false, error: `Image generation failed (${res.status}): ${text.slice(0, 200) || res.statusText}` }
    }
    const body = await res.json()
    // OpenAI-compatible: { data: [{ b64_json, revised_prompt }] }
    if (Array.isArray(body?.data) && body.data.length > 0 && body.data[0]?.b64_json) {
      return { success: true, imageData: body.data[0].b64_json, revisedPrompt: body.data[0].revised_prompt }
    }
    // Alternative: { image_data, revised_prompt }
    if (typeof body?.image_data === 'string') {
      return { success: true, imageData: body.image_data, revisedPrompt: body.revised_prompt }
    }
    return { success: false, error: 'No image data in response' }
  } catch (e) {
    return { success: false, error: (e as Error).message }
  }
}

// ── Web Search ──────────────────────────────────────────────────

export interface WebSearchResult {
  success: boolean
  query?: string
  results?: string
  model?: string
  searchType?: string
  sources?: Array<{ uri?: string; title?: string }>
  error?: string
}

/**
 * Run a web search against the configured engine.
 * Mirrors POST /web_search; search_type values: "openai_search" | "google_search".
 */
export async function webSearch(query: string, searchType: string): Promise<WebSearchResult> {
  if (!query.trim()) {
    return { success: false, error: 'Query is required' }
  }
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/web_search`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: query.trim(),
        search_type: searchType,
        // Default `model` matches the backend's WebSearchRequest default
        // ("gpt-4o" — only used by the OpenAI handler; ignored for Google).
        model: searchType === 'google_search' ? 'gemini-2.5-flash' : 'gpt-4o',
      }),
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      return { success: false, error: `Search failed (${res.status}): ${text.slice(0, 200) || res.statusText}` }
    }
    const body = await res.json()
    if (body?.error) {
      return { success: false, error: body.error.message || 'Search error' }
    }
    return {
      success: body?.success !== false,
      query: body?.query,
      results: body?.results,
      model: body?.model,
      searchType: body?.search_type,
      sources: Array.isArray(body?.sources) ? body.sources : undefined,
    }
  } catch (e) {
    return { success: false, error: (e as Error).message }
  }
}

export interface UploadBlobResult {
  ok: boolean
  key?: string
  size?: number
  contentType?: string
  status?: number
  error?: string
}

/**
 * Upload binary data as a blob attached to a cloud entity (note, task, …).
 * Returns granular result so callers can surface upload errors to the user.
 * Mirrors the backend's POST /cloud_data/blob/upload (multipart form).
 */
export async function uploadBlob(
  bytes: Uint8Array,
  filename: string,
  contentType: string,
  entityType: 'notes' | 'tasks' | 'locations' | 'contacts' | 'personas',
  entityId: string,
): Promise<UploadBlobResult> {
  try {
    const token = await getIdToken()
    if (!token) return { ok: false, error: 'Not authenticated' }
    const form = new FormData()
    const blob = new Blob([new Uint8Array(bytes.buffer as ArrayBuffer, bytes.byteOffset, bytes.byteLength)], { type: contentType })
    form.append('file', blob, filename)
    form.append('entity_type', entityType)
    form.append('entity_id', entityId)
    const res = await fetch(`${API_BASE}/cloud_data/blob/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      return { ok: false, status: res.status, error: text.slice(0, 240) || res.statusText }
    }
    const data = await res.json()
    if (typeof data?.key !== 'string') {
      return { ok: false, status: res.status, error: 'No key returned from backend' }
    }
    return { ok: true, key: data.key, size: data.size, contentType: data.content_type }
  } catch (e) {
    console.error('uploadBlob failed', e)
    return { ok: false, error: (e as Error).message }
  }
}

/**
 * Delete a blob from R2 by key. Mirrors the backend's
 * `DELETE /cloud_data/blob?key=<key>` route. Returns true on success.
 *
 * Used when the user removes an attached image / document so the storage
 * doesn't accumulate orphan blobs. Best-effort: if the network call fails
 * the caller still proceeds with the metadata removal — a stale blob is
 * cheaper than a failed deletion the user can't retry.
 */
export async function deleteBlob(key: string): Promise<boolean> {
  if (!key) return false
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/cloud_data/blob?key=${encodeURIComponent(key)}`, {
      method: 'DELETE',
      headers,
    })
    return res.ok
  } catch (e) {
    console.warn('deleteBlob failed', key, e)
    return false
  }
}

// ── Account / Subscription / Coupons ────────────────────────────

export interface UsageBreakdownItem {
  action_type: string
  display_name: string
  count: number
  unit_cost: number
  total_credits: number
}

export interface QuotaUsage {
  available: number
  used_this_month: number
  monthly_limit: number
  addon_remaining: number
  percentage_used: number
}

export interface QuotaStatus {
  user_id: string
  subscription_tier: string
  credits_left: number
  credit_percentage_left: number
  monthly_credits?: number
  deposit_credits?: number
  warnings: string[]
  quota_summary: Record<string, QuotaUsage>
  usage_breakdown?: { items: UsageBreakdownItem[]; total_credits_used: number }
}

export interface UsagePeriod {
  year: number
  month: number
  label: string
  start: string
  end: string
}

export interface PeriodUsageBreakdown {
  period: UsagePeriod
  usage_breakdown: { items: UsageBreakdownItem[]; total_credits_used: number }
}

/** Fetch usage breakdown for a specific billing period (year + month). */
export async function getUsageBreakdownForPeriod(
  userEmail: string,
  year: number,
  month: number,
): Promise<PeriodUsageBreakdown | null> {
  try {
    const headers = await authHeaders()
    const res = await fetch(
      `${API_BASE}/quota/usage-breakdown?user_id=${encodeURIComponent(userEmail)}&year=${year}&month=${month}`,
      { method: 'GET', headers },
    )
    if (!res.ok) return null
    const data = await res.json()
    return {
      period: {
        year: data?.period?.year ?? year,
        month: data?.period?.month ?? month,
        label: data?.period?.label ?? '',
        start: data?.period?.start ?? '',
        end: data?.period?.end ?? '',
      },
      usage_breakdown: {
        items: Array.isArray(data?.usage_breakdown?.items) ? data.usage_breakdown.items : [],
        total_credits_used: data?.usage_breakdown?.total_credits_used ?? 0,
      },
    }
  } catch {
    return null
  }
}

/** Fetch the user's quota / subscription status (tier, credits, usage breakdown). */
export async function getQuotaStatus(userEmail: string): Promise<QuotaStatus | null> {
  try {
    const headers = await authHeaders()
    const res = await fetch(
      `${API_BASE}/quota/status?user_id=${encodeURIComponent(userEmail)}`,
      { method: 'GET', headers },
    )
    if (!res.ok) return null
    const data = await res.json()
    return {
      user_id: data.user_id || userEmail,
      subscription_tier: data.subscription_tier || 'free',
      credits_left: data.credits_left ?? 0,
      credit_percentage_left: data.credit_percentage_left ?? 0,
      monthly_credits: data.monthly_credits,
      deposit_credits: data.deposit_credits,
      warnings: Array.isArray(data.warnings) ? data.warnings : [],
      quota_summary: data.quota_summary || {},
      usage_breakdown: data.usage_breakdown,
    }
  } catch {
    return null
  }
}

export interface CouponRedemptionResult {
  success: boolean
  message: string
  credits_added?: number
  subscription_extended?: number
}

/** Redeem a coupon code for the current user. */
export async function redeemCoupon(userEmail: string, code: string): Promise<CouponRedemptionResult> {
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/coupon/redeem`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_id: userEmail, code: code.trim().toUpperCase() }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      return { success: false, message: data?.detail || `Failed (${res.status})` }
    }
    return {
      success: data?.success !== false,
      message: data?.message || 'Coupon redeemed',
      credits_added: data?.credits_added,
      subscription_extended: data?.subscription_extended,
    }
  } catch (e) {
    return { success: false, message: `Error redeeming coupon: ${(e as Error).message}` }
  }
}

/** Delete the user's account and all associated cloud data. */
export async function deleteAccount(userEmail: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/user/delete_account`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email: userEmail }),
    })
    if (res.ok || res.status === 404) {
      // 404 = user already absent on backend; treat as success for cleanup flow.
      return { ok: true }
    }
    const data = await res.json().catch(() => ({}))
    return { ok: false, error: data?.detail || `Server error ${res.status}` }
  } catch (e) {
    return { ok: false, error: (e as Error).message }
  }
}

// ── Debug: Message History Context ──────────────────────────────

export async function fetchDebugMessageHistory(
  voiceChatId: string,
): Promise<Record<string, unknown>> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(`${API_BASE}/voice_chat/debug/message_history`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ voice_chat_id: voiceChatId }),
  })

  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Debug message history failed (${res.status}): ${error.slice(0, 200)}`)
  }

  return res.json()
}

// ── Session Costs (for test automation) ─────────────────────────

export async function getSessionCosts(
  voiceChatId: string,
): Promise<Record<string, unknown> | null> {
  const headers = await authHeaders()

  try {
    const res = await fetch(`${API_BASE}/costs/session/${voiceChatId}/costs`, {
      method: 'GET',
      headers,
    })
    if (res.ok) return res.json()
    return null
  } catch {
    return null
  }
}

// ── Cloud Data Sync ─────────────────────────────────────────────

import type {
  CloudTask,
  CloudProject,
  CloudNote,
  CloudEmail,
  CloudContact,
  CloudLocation,
} from '../types'
import { decryptJson, encryptString, decryptBytes } from './encryption'

/** Decrypt a list of cloud items, filtering out any that fail decryption. */
async function decryptItems<T>(items: T[]): Promise<T[]> {
  const results: T[] = []
  for (const item of items) {
    try {
      const decrypted = await decryptJson(item as Record<string, unknown>)
      if (decrypted) results.push(decrypted as T)
    } catch (e) {
      console.warn('Skipping item that failed decryption:', e)
    }
  }
  return results
}

export async function listCloudTasks(since?: string): Promise<CloudTask[]> {
  const headers = await authHeaders()
  const params = since ? `?since=${encodeURIComponent(since)}` : ''
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/tasks${params}`, { method: 'GET', headers })
  if (!res.ok) {
    if (res.status === 404) return []
    throw new Error(`List tasks failed (${res.status})`)
  }
  const data = await res.json()
  return decryptItems<CloudTask>(data.tasks || [])
}

export async function getCloudTask(taskId: string): Promise<CloudTask | null> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/tasks/${taskId}`, { method: 'GET', headers })
  if (!res.ok) return null
  const raw = await res.json()
  return (await decryptJson(raw)) as CloudTask | null
}

export async function listCloudProjects(since?: string): Promise<CloudProject[]> {
  const headers = await authHeaders()
  const params = since ? `?since=${encodeURIComponent(since)}` : ''
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/projects${params}`, { method: 'GET', headers })
  if (!res.ok) {
    if (res.status === 404) return []
    throw new Error(`List projects failed (${res.status})`)
  }
  const data = await res.json()
  return decryptItems<CloudProject>(data.projects || [])
}

/**
 * Normalize a note from cloud format (camelCase from Flutter's toJson())
 * to the snake_case format used by the web client's CloudNote interface.
 * Handles both naming conventions gracefully.
 */
function normalizeNote(raw: Record<string, unknown>): CloudNote {
  const n = raw as CloudNote & Record<string, unknown>
  // Map camelCase → snake_case (Flutter toJson() uses camelCase)
  if (n.isAiGeneratedDocument !== undefined && n.is_ai_generated_document === undefined) {
    n.is_ai_generated_document = !!n.isAiGeneratedDocument
  }
  if (n.isAiGeneratedImage !== undefined && n.is_ai_generated_image === undefined) {
    n.is_ai_generated_image = !!n.isAiGeneratedImage
  }
  if (n.isGeneratedPdf !== undefined && n.is_generated_pdf === undefined) {
    n.is_generated_pdf = !!n.isGeneratedPdf
  }
  if (n.isApp !== undefined && n.is_app === undefined) {
    n.is_app = !!n.isApp
  }
  if (n.createdAt !== undefined && n.created_at === undefined) {
    n.created_at = n.createdAt as string
  }
  if (n.lastUpdatedAt !== undefined && n.last_updated_at === undefined) {
    n.last_updated_at = n.lastUpdatedAt as string
  }
  // Also handle integer truthy values (SQLite stores 0/1)
  if (typeof n.is_ai_generated_document === 'number') n.is_ai_generated_document = n.is_ai_generated_document !== 0
  if (typeof n.is_ai_generated_image === 'number') n.is_ai_generated_image = n.is_ai_generated_image !== 0
  if (typeof n.is_generated_pdf === 'number') n.is_generated_pdf = n.is_generated_pdf !== 0
  if (typeof n.is_app === 'number') n.is_app = (n.is_app as unknown as number) !== 0

  // Normalize attachments (Flutter uses camelCase: filePath, createdAt)
  if (Array.isArray(n.attachments)) {
    n.attachments = n.attachments.map((att: Record<string, unknown>) => {
      const a = att as Record<string, unknown>
      if (a.filePath !== undefined && a.file_path === undefined) {
        a.file_path = a.filePath as string
      }
      if (a.blobKey !== undefined && a.blob_key === undefined) {
        a.blob_key = a.blobKey as string
      }
      if (a.createdAt !== undefined && a.created_at === undefined) {
        a.created_at = a.createdAt as string
      }
      return a as CloudNote['attachments'] extends Array<infer T> ? T : never
    })
  }

  return n
}

export async function listCloudNotes(type?: number, since?: string): Promise<CloudNote[]> {
  const headers = await authHeaders()
  const params = new URLSearchParams()
  if (type !== undefined) params.set('type', String(type))
  if (since) params.set('since', since)
  const qs = params.toString() ? `?${params}` : ''
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/notes${qs}`, { method: 'GET', headers })
  if (!res.ok) {
    if (res.status === 404) return []
    throw new Error(`List notes failed (${res.status})`)
  }
  const data = await res.json()
  const decrypted = await decryptItems<CloudNote>(data.notes || [])
  return decrypted.map((n) => normalizeNote(n as unknown as Record<string, unknown>))
}

export async function getCloudNote(noteId: string): Promise<CloudNote | null> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/notes/${noteId}`, { method: 'GET', headers })
  if (!res.ok) return null
  const raw = await res.json()
  const decrypted = await decryptJson(raw)
  if (!decrypted) return null
  return normalizeNote(decrypted)
}

export async function listCloudEmails(direction?: string, since?: string): Promise<CloudEmail[]> {
  const headers = await authHeaders()
  const params = new URLSearchParams()
  if (direction) params.set('direction', direction)
  if (since) params.set('since', since)
  const qs = params.toString() ? `?${params}` : ''
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/emails${qs}`, { method: 'GET', headers })
  if (!res.ok) {
    if (res.status === 404) return []
    throw new Error(`List emails failed (${res.status})`)
  }
  const data = await res.json()
  return decryptItems<CloudEmail>(data.emails || [])
}

// ── Cloud Sync State ────────────────────────────────────────────

/** Enable or disable server-side cloud sync for the current user. Toggling
 * this flag on is required for the backend to persist inbound/sent emails
 * to cloud_emails, which the web client reads. */
export async function setCloudSyncEnabled(enabled: boolean): Promise<boolean> {
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/cloud_data/sync/state`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ enabled }),
    })
    return res.ok
  } catch {
    return false
  }
}

// ── Outbound Email ──────────────────────────────────────────────

/** Get the user's PiPar mail status (whether email integration is enabled
 * and the assigned mail address). The backend derives the user_id from the
 * auth token, but the route requires it in the query string too. */
export async function getEmailStatus(userId: string): Promise<{ enabled: boolean; address: string }> {
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/email/status?user_id=${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers,
    })
    if (!res.ok) return { enabled: false, address: '' }
    const data = await res.json()
    return { enabled: !!data.enabled, address: data.address || '' }
  } catch {
    return { enabled: false, address: '' }
  }
}

export interface SendContactMessageParams {
  userDisplayName: string
  userPiparEmail: string
  toEmail: string
  subject: string
  message: string
  priority?: string
}

/** Send an email via the backend /contacts/send_message route. The backend
 * routes internally (PiPar → PiPar) or externally (Mailgun) based on the
 * recipient domain. Used by the email review dialog after user approval.
 *
 * Passes `save_sent_to_cloud: true` so the backend mirrors the sent copy to
 * cloud_emails — the web client has no local storage and relies on that to
 * show the message in its Sent tab. Existing Flutter apps don't set this
 * field (default False) and keep their own cloud_sync_service as the sole
 * writer of sent rows. */
export async function sendContactMessage(params: SendContactMessageParams): Promise<{ success: boolean; routing?: string; mail_id?: string }> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(`${API_BASE}/contacts/send_message`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      user_display_name: params.userDisplayName,
      user_pipar_email: params.userPiparEmail,
      to_email: params.toEmail,
      subject: params.subject,
      message: params.message,
      priority: params.priority || 'normal',
      save_sent_to_cloud: true,
    }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Send failed (${res.status}): ${text.slice(0, 200)}`)
  }
  return await res.json()
}

export async function listCloudContacts(since?: string): Promise<CloudContact[]> {
  const headers = await authHeaders()
  const params = since ? `?since=${encodeURIComponent(since)}` : ''
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/contacts${params}`, { method: 'GET', headers })
  if (!res.ok) {
    if (res.status === 404) return []
    throw new Error(`List contacts failed (${res.status})`)
  }
  const data = await res.json()
  return decryptItems<CloudContact>(data.contacts || [])
}

/** Get blob keys for persona profile images. Returns {persona_id: blob_key} map. */
export async function getPersonaImageKeys(): Promise<Record<string, string>> {
  const headers = await authHeaders()
  try {
    const res = await fetchWithRetry(`${API_BASE}/cloud_data/persona_images`, { method: 'GET', headers })
    if (!res.ok) return {}
    const data = await res.json()
    return data.images || {}
  } catch {
    return {}
  }
}

/** List blob keys for an entity type, optionally filtered by entity ID. */
export async function listBlobKeys(entityType: string, entityId = ''): Promise<string[]> {
  const headers = await authHeaders()
  try {
    const params = new URLSearchParams({ entity_type: entityType })
    if (entityId) params.set('entity_id', entityId)
    const res = await fetch(`${API_BASE}/cloud_data/blob/list?${params}`, { headers })
    if (!res.ok) return []
    const data = await res.json()
    return data.keys || []
  } catch {
    return []
  }
}

/** Get a pre-signed direct URL from Cloudflare R2 for a blob key. Cached for reuse within expiry. */
const presignedUrlCache = new Map<string, { url: string; expiresAt: number }>()

export async function getBlobDirectUrl(key: string): Promise<string | null> {
  // Check cache (use with 5 min safety margin)
  const cached = presignedUrlCache.get(key)
  if (cached && cached.expiresAt > Date.now() + 300_000) return cached.url

  const headers = await authHeaders()
  try {
    const res = await fetch(`${API_BASE}/cloud_data/blob/url?key=${encodeURIComponent(key)}`, { headers })
    if (!res.ok) return null
    const data = await res.json()
    if (data.url) {
      presignedUrlCache.set(key, { url: data.url, expiresAt: Date.now() + (data.expires_in || 3600) * 1000 })
      return data.url
    }
    return null
  } catch {
    return null
  }
}

/** Batch-fetch pre-signed URLs for multiple blob keys in one request. */
export async function getBlobDirectUrls(keys: string[]): Promise<Record<string, string>> {
  if (keys.length === 0) return {}
  const headers = await authHeaders()
  try {
    const res = await fetchWithRetry(`${API_BASE}/cloud_data/blob/urls`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ keys }),
    })
    if (!res.ok) return {}
    const data = await res.json()
    const urls = data.urls || {}
    const expiresAt = Date.now() + (data.expires_in || 3600) * 1000
    for (const [key, url] of Object.entries(urls)) {
      presignedUrlCache.set(key, { url: url as string, expiresAt })
    }
    return urls
  } catch {
    return {}
  }
}

/**
 * Download raw blob bytes. Tries direct Cloudflare URL first, falls back to backend proxy.
 */
export async function downloadBlob(key: string): Promise<Uint8Array | null> {
  // Try direct Cloudflare fetch first
  const directUrl = await getBlobDirectUrl(key)
  if (directUrl) {
    try {
      const res = await fetch(directUrl)
      if (res.ok) return new Uint8Array(await res.arrayBuffer())
    } catch { /* fall through to backend proxy */ }
  }

  // Fallback: backend proxy
  const headers = await authHeaders()
  try {
    const res = await fetch(`${API_BASE}/cloud_data/blob/raw?key=${encodeURIComponent(key)}`, { headers })
    if (!res.ok) return null
    return new Uint8Array(await res.arrayBuffer())
  } catch {
    return null
  }
}

/**
 * Download a blob, decrypt if needed, and return a displayable URL.
 * Always downloads and decrypts since blobs are encrypted in R2.
 * Returns null if the blob doesn't exist or can't be loaded.
 */
export async function downloadBlobAsUrl(key: string): Promise<{ url: string; mimeType: string } | null> {
  const bytes = await downloadBlob(key)
  if (!bytes || bytes.length === 0) return null

  // Check if bytes are already a known raw format (not encrypted)
  const isRawMedia =
    (bytes[0] === 0x89 && bytes[1] === 0x50) || // PNG
    (bytes[0] === 0xFF && bytes[1] === 0xD8) || // JPEG
    (bytes[0] === 0x47 && bytes[1] === 0x49) || // GIF
    (bytes[0] === 0x52 && bytes[1] === 0x49) || // RIFF (WAV/WebP)
    (bytes[0] === 0x49 && bytes[1] === 0x44) || // ID3 (MP3)
    (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) // ftyp (M4A/MP4)

  let finalBytes = bytes
  if (!isRawMedia) {
    const decrypted = await decryptBytes(bytes)
    if (decrypted) finalBytes = decrypted
  }

  // Detect MIME type from magic bytes
  let mimeType = 'application/octet-stream'
  if (finalBytes[0] === 0x89 && finalBytes[1] === 0x50) mimeType = 'image/png'
  else if (finalBytes[0] === 0xFF && finalBytes[1] === 0xD8) mimeType = 'image/jpeg'
  else if (finalBytes[0] === 0x47 && finalBytes[1] === 0x49) mimeType = 'image/gif'
  else if (finalBytes[0] === 0x52 && finalBytes[1] === 0x49 && finalBytes[8] === 0x57 && finalBytes[9] === 0x41) mimeType = 'audio/wav' // RIFF....WAVE
  else if (finalBytes[0] === 0x52 && finalBytes[1] === 0x49) mimeType = 'image/webp' // other RIFF
  else if (finalBytes[4] === 0x66 && finalBytes[5] === 0x74 && finalBytes[6] === 0x79 && finalBytes[7] === 0x70) mimeType = 'audio/mp4' // ftyp (M4A)
  else if (finalBytes[0] === 0x49 && finalBytes[1] === 0x44) mimeType = 'audio/mpeg' // ID3 (MP3)
  else if (finalBytes[0] === 0xFF && (finalBytes[1] & 0xE0) === 0xE0) mimeType = 'audio/mpeg' // MP3 sync
  else if (finalBytes[0] === 0x4F && finalBytes[1] === 0x67) mimeType = 'audio/ogg' // OggS
  else if (finalBytes[0] === 0x25 && finalBytes[1] === 0x50) mimeType = 'application/pdf'
  else {
    // Also detect by file extension from key
    const lower = key.toLowerCase()
    if (lower.endsWith('.m4a') || lower.endsWith('.aac')) mimeType = 'audio/mp4'
    else if (lower.endsWith('.wav')) mimeType = 'audio/wav'
    else if (lower.endsWith('.mp3')) mimeType = 'audio/mpeg'
    else if (lower.endsWith('.ogg') || lower.endsWith('.opus')) mimeType = 'audio/ogg'
    else {
      const textSample = new TextDecoder().decode(finalBytes.slice(0, 100)).trim().toLowerCase()
      if (textSample.startsWith('<!doctype') || textSample.startsWith('<html') || textSample.startsWith('<div') || textSample.startsWith('<h1')) {
        mimeType = 'text/html'
      }
    }
  }

  const blob = new Blob([new Uint8Array(finalBytes.buffer as ArrayBuffer, finalBytes.byteOffset, finalBytes.byteLength)], { type: mimeType })
  return { url: URL.createObjectURL(blob), mimeType }
}

/**
 * Download a blob and return it as decoded text (for HTML docs).
 * Returns null if the blob doesn't exist or can't be loaded.
 */
export async function downloadBlobAsText(key: string): Promise<string | null> {
  const bytes = await downloadBlob(key)
  if (!bytes || bytes.length === 0) return null

  // Always try decryption first, then check if result is valid text.
  // Encrypted format: 16-byte IV + ciphertext — first bytes are random,
  // so we can't reliably detect encryption from magic bytes alone.
  const decrypted = await decryptBytes(bytes)
  if (decrypted && decrypted.length > 0) {
    const text = new TextDecoder().decode(decrypted)
    // Sanity check: decrypted text should be mostly printable
    if (text.length > 0 && !text.startsWith('\0')) return text
  }

  // Decryption failed or produced garbage — try as raw text
  const raw = new TextDecoder().decode(bytes)
  // Check if it looks like actual text (starts with a text-like character)
  if (raw.length > 0 && (raw[0] === '<' || raw[0] === '{' || raw[0] === '#' || raw.charCodeAt(0) >= 0x20)) {
    return raw
  }

  return null
}

export async function listCloudLocations(since?: string): Promise<CloudLocation[]> {
  const headers = await authHeaders()
  const params = since ? `?since=${encodeURIComponent(since)}` : ''
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/locations${params}`, { method: 'GET', headers })
  if (!res.ok) {
    if (res.status === 404) return []
    throw new Error(`List locations failed (${res.status})`)
  }
  const data = await res.json()
  return decryptItems<CloudLocation>(data.locations || [])
}

export interface UserProfile {
  user_name: string
  additional_details: string
}

export async function getUserProfile(): Promise<UserProfile> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(`${API_BASE}/user/profile`, { method: 'GET', headers })
  if (!res.ok) {
    if (res.status === 404) return { user_name: '', additional_details: '' }
    throw new Error(`Get user profile failed (${res.status})`)
  }
  const data = await res.json()
  return {
    user_name: data.user_name || '',
    additional_details: data.additional_details || '',
  }
}

export async function saveUserProfile(profile: UserProfile): Promise<void> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(`${API_BASE}/user/profile`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(profile),
  })
  if (!res.ok) throw new Error(`Save user profile failed (${res.status})`)
}

// ── Cloud Data Write Operations ────────────────────────────────

/** Encrypt an item and send it as a PUT upsert. */
async function encryptAndPut(url: string, item: Record<string, unknown>): Promise<void> {
  const headers = await authHeaders()
  const encrypted = await encryptString(JSON.stringify(item))
  if (!encrypted) throw new Error('Encryption failed')
  const res = await fetchWithRetry(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ data: { id: item.id, _encrypted: encrypted } }),
  })
  if (!res.ok) {
    const error = await res.text()
    throw new Error(`PUT failed (${res.status}): ${error.slice(0, 200)}`)
  }
}

/** Delete a cloud data item by URL. */
async function cloudDelete(url: string): Promise<void> {
  const headers = await authHeaders()
  const res = await fetchWithRetry(url, { method: 'DELETE', headers })
  if (!res.ok) {
    const error = await res.text()
    throw new Error(`DELETE failed (${res.status}): ${error.slice(0, 200)}`)
  }
}

export async function upsertCloudTask(task: CloudTask): Promise<void> {
  await encryptAndPut(`${API_BASE}/cloud_data/tasks/${task.id}`, task as Record<string, unknown>)
}

export async function deleteCloudTask(taskId: string): Promise<void> {
  await cloudDelete(`${API_BASE}/cloud_data/tasks/${taskId}`)
}

// ── Checklists (subtasks) ─────────────────────────────────────
// Subtasks live in cloud_checklists (one row per task; full Checklist
// JSON in `data`). The mobile app's `Checklist`/`ChecklistItem` shape:
//   { id, title, taskId, items: [{ id, title, isCompleted, createdAt,
//     completedAt, sortOrder }], createdAt, lastUpdatedAt, createdBy }
//
// Stored unencrypted server-side — uses the plain CRUD helpers, not
// `encryptAndPut`. Mirrors the desktop `ChecklistProvider` flow.

export interface CloudChecklistItem {
  id: string
  title: string
  isCompleted: boolean
  createdAt?: string
  completedAt?: string
  sortOrder?: number
}

export interface CloudChecklist {
  id: string
  title?: string
  taskId: string
  items: CloudChecklistItem[]
  createdAt?: string
  lastUpdatedAt?: string
  createdBy?: string
}

export async function listCloudChecklists(since?: string): Promise<CloudChecklist[]> {
  const headers = await authHeaders()
  const params = since ? `?since=${encodeURIComponent(since)}` : ''
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/checklists${params}`, { method: 'GET', headers })
  if (!res.ok) {
    if (res.status === 404) return []
    throw new Error(`List checklists failed (${res.status})`)
  }
  const data = await res.json()
  return (data.checklists || []) as CloudChecklist[]
}

export async function getChecklistForTask(taskId: string): Promise<CloudChecklist | null> {
  const headers = await authHeaders()
  const url = `${API_BASE}/cloud_data/checklists?task_id=${encodeURIComponent(taskId)}`
  const res = await fetchWithRetry(url, { method: 'GET', headers })
  if (!res.ok) return null
  const data = await res.json()
  const list = (data.checklists || []) as CloudChecklist[]
  return list.length > 0 ? list[0] : null
}

export async function upsertCloudChecklist(checklist: CloudChecklist): Promise<void> {
  const headers = await authHeaders()
  // Plaintext PUT — backend `update_checklist` accepts `{ data: {...} }`
  // to match the SingleItemRequest shape used by tasks/notes/etc.
  const res = await fetchWithRetry(`${API_BASE}/cloud_data/checklists/${checklist.id}`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: checklist }),
  })
  if (!res.ok) {
    const error = await res.text()
    throw new Error(`Checklist PUT failed (${res.status}): ${error.slice(0, 200)}`)
  }
}

export async function deleteCloudChecklist(checklistId: string): Promise<void> {
  await cloudDelete(`${API_BASE}/cloud_data/checklists/${checklistId}`)
}

export async function upsertCloudNote(note: CloudNote): Promise<void> {
  await encryptAndPut(`${API_BASE}/cloud_data/notes/${note.id}`, note as Record<string, unknown>)
}

export async function deleteCloudNote(noteId: string): Promise<void> {
  await cloudDelete(`${API_BASE}/cloud_data/notes/${noteId}`)
}

export async function upsertCloudProject(project: CloudProject): Promise<void> {
  await encryptAndPut(`${API_BASE}/cloud_data/projects/${project.id}`, project as unknown as Record<string, unknown>)
}

export async function deleteCloudProject(projectId: string): Promise<void> {
  await cloudDelete(`${API_BASE}/cloud_data/projects/${projectId}`)
}

export async function deleteCloudContact(contactId: string): Promise<void> {
  await cloudDelete(`${API_BASE}/cloud_data/contacts/${contactId}`)
}

export async function upsertCloudLocation(location: CloudLocation): Promise<void> {
  await encryptAndPut(`${API_BASE}/cloud_data/locations/${location.id}`, location as unknown as Record<string, unknown>)
}

export async function deleteCloudLocation(locationId: string): Promise<void> {
  await cloudDelete(`${API_BASE}/cloud_data/locations/${locationId}`)
}

// ── Tool Model (home quick line) ───────────────────────────────

/**
 * One-shot structured call for the home screen quick line. No persona, no
 * chat history — just a system prompt and the utterance. Returns the raw
 * text of the model's reply (expected to be JSON), or null on failure.
 */
export async function runToolCompletion(
  systemPrompt: string,
  userMessage: string,
  model: string = DEFAULT_TITLE_MODEL,
): Promise<string | null> {
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE}/completions`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userMessage },
        ],
        max_tokens: 400,
        temperature: 0,
      }),
    })
    if (!res.ok) {
      console.warn('Tool completion failed:', res.status, await res.text().catch(() => ''))
      return null
    }
    const data = await res.json()
    const text = data?.choices?.[0]?.message?.content
    return typeof text === 'string' ? text : null
  } catch (e) {
    console.warn('Tool completion error:', e)
    return null
  }
}

// ── AI content reports ──────────────────────────────────────────

export type ReportReason =
  | 'sexual' | 'violent' | 'hateful' | 'self_harm' | 'dangerous' | 'misinformation' | 'other'

export interface ReportContentParams {
  contentType: 'image' | 'text'
  reason: ReportReason
  /** The AI response text, or for an image the prompt it was made from. */
  aiContent: string
  userRequest?: string
  comment?: string
  /** Raw base64 (no data: prefix). */
  imageBase64?: string
  imageMimeType?: string
  messageId?: string
  /** Where the content was seen: 'chat', 'document', 'note', ... */
  source?: string
}

/** Backend caps ai_content at 20 000 characters; long documents are cut. */
const MAX_REPORT_AI_CHARS = 19000

function truncateForReport(text: string, max = MAX_REPORT_AI_CHARS): string {
  return text.length <= max ? text : `${text.slice(0, max)}\n\n[... truncated, ${text.length} characters in total]`
}

/**
 * Report offensive AI-generated content to PiPar support (Google Play AI
 * content policy; same /report_content route the mobile app uses). The
 * backend forwards it by email. Returns null on success, else an error code.
 */
export async function reportContent(
  params: ReportContentParams,
): Promise<null | 'auth' | 'rate_limited' | 'failed'> {
  let headers: Record<string, string>
  try {
    headers = await authHeaders()
  } catch {
    return 'auth'
  }
  try {
    const res = await fetch(`${API_BASE}/report_content`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        content_type: params.contentType,
        reason: params.reason,
        ai_content: truncateForReport(params.aiContent),
        user_request: params.userRequest?.trim() ? truncateForReport(params.userRequest, 9000) : undefined,
        comment: params.comment?.trim() ? params.comment : undefined,
        image_base64: params.imageBase64,
        image_mime_type: params.imageBase64 ? (params.imageMimeType || 'image/png') : undefined,
        platform: 'web',
        language: navigator.language,
        message_id: params.messageId,
        source: params.source,
      }),
    })
    if (res.ok) return null
    console.warn('Report failed:', res.status, await res.text().catch(() => ''))
    return res.status === 429 ? 'rate_limited' : 'failed'
  } catch (e) {
    console.warn('Report error:', e)
    return 'failed'
  }
}
